import { useEffect, useState } from "react";
import { X, AlertCircle } from "lucide-react";

/**
 * Modal genérico para cambiar el estatus de cualquier recurso (usuario,
 * representante). Acepta la lista de opciones de estatus disponibles según el
 * recurso y un motivo obligatorio.
 */
export function CambiarEstatusModal({
  open,
  title,
  estatusActual,
  opciones,
  aviso,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  estatusActual: string;
  opciones: { value: string; label: string; description?: string }[];
  /**
   * Advertencia que depende del estatus elegido — p. ej. a quién deja sin
   * cuenta una baja. Se muestra entre las opciones y el motivo, para que el
   * impacto se lea antes de escribir la justificación y pulsar Aplicar.
   */
  aviso?: (nuevoEstatus: string) => React.ReactNode;
  onCancel: () => void;
  onConfirm: (nuevoEstatus: string, motivo: string) => Promise<void>;
}) {
  const [nuevo, setNuevo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const primera = opciones.find((o) => o.value !== estatusActual);
      setNuevo(primera ? primera.value : "");
      setMotivo("");
      setError(null);
    }
  }, [open, estatusActual, opciones]);

  if (!open) return null;

  const submit = async () => {
    if (!nuevo) {
      setError("Selecciona el nuevo estatus.");
      return;
    }
    if (!motivo.trim()) {
      setError("El motivo es obligatorio.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onConfirm(nuevo, motivo.trim());
    } catch (e: unknown) {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Alto acotado con el cuerpo desplazable: la advertencia de impacto puede
          crecer con la lista de cuentas y el pie no debe quedar fuera de vista. */}
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded p-1 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <div className="text-sm text-fg-muted">
            Estatus actual: <span className="font-semibold">{estatusActual}</span>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
              Nuevo estatus
            </label>
            <div className="mt-1 space-y-1">
              {opciones
                .filter((o) => o.value !== estatusActual)
                .map((o) => (
                  <label
                    key={o.value}
                    className={`flex cursor-pointer items-start gap-2 rounded border p-2 text-sm ${
                      nuevo === o.value
                        ? "border-primary-strong bg-estado-info-bg"
                        : "border-edge hover:border-edge-strong"
                    }`}
                  >
                    <input
                      type="radio"
                      name="estatus"
                      checked={nuevo === o.value}
                      onChange={() => setNuevo(o.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-fg">{o.label}</div>
                      {o.description && <div className="text-xs text-fg-muted">{o.description}</div>}
                    </div>
                  </label>
                ))}
            </div>
          </div>
          {aviso && nuevo && aviso(nuevo)}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
              Motivo <span className="text-estado-err">*</span>
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
              placeholder="Detalla la razón del cambio (queda en bitácora)"
              disabled={loading}
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Aplicando…" : "Aplicar"}
          </button>
        </div>
      </div>
    </div>
  );
}
